import { Haptics, ImpactStyle } from '@capacitor/haptics'

import React, { useEffect, useRef, useState } from 'react'

import { FormFieldDTO } from '../models/FormFieldDTO.model'

const RATING_EMOJIS = ['😩', '🙁', '😐', '🙂', '😄'] as const
const RATING_LABELS = ['Poor', 'Fair', 'Okay', 'Good', 'Great'] as const
const AUTO_ADVANCE_HOLD_MS = 500

type ReviewRatingFieldProps = {
  field: FormFieldDTO
  fieldValue?: unknown
  onChange: (fieldId: number | string, value: unknown) => void
  onRatingSelected?: (fieldId: number, value: number) => void
  validationError?: string | JSX.Element
}

export const ReviewRatingField: React.FC<ReviewRatingFieldProps> = ({
  field,
  fieldValue,
  onChange,
  onRatingSelected,
  validationError,
}) => {
  const initialSelected = fieldValue ? Number(fieldValue) : null
  const [selected, setSelected] = useState<number | null>(initialSelected)
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
      setSelected(Number(fieldValue))
    }
  }, [fieldValue])

  useEffect(() => {
    return () => {
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current)
      }
    }
  }, [])

  const choiceCount = field.choices?.length ?? RATING_EMOJIS.length

  const handleSelect = (rating: number) => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current)
    }

    setSelected(rating)
    onChange(field.id, rating)
    Haptics.impact({ style: ImpactStyle.Light })

    if (!onRatingSelected) {
      return
    }

    holdTimerRef.current = setTimeout(() => {
      onRatingSelected(field.id, rating)
    }, AUTO_ADVANCE_HOLD_MS)
  }

  return (
    <div id={`review-input-${field.id}`} className="mb-4">
      <p className="mb-6 text-center text-sm leading-relaxed text-gray-600 md:text-base">{field.label}</p>
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        {field.choices?.map((choice, choiceIndex) => {
          const rating = choiceIndex + 1
          const isSelected = rating === selected
          const emoji = RATING_EMOJIS[choiceIndex] ?? '😐'
          const label = RATING_LABELS[choiceIndex] ?? choice.label

          return (
            <button
              key={choice.value}
              type="button"
              aria-label={`${label}, ${rating} of ${choiceCount}`}
              aria-pressed={isSelected}
              className="flex min-w-0 flex-1 flex-col items-center px-1 py-2"
              onClick={() => handleSelect(rating)}
            >
              <span
                className={`flex h-12 w-12 items-center justify-center rounded-full border-2 text-3xl leading-none transition-all duration-150 sm:h-14 sm:w-14 sm:text-4xl motion-reduce:transition-none motion-reduce:transform-none ${
                  isSelected
                    ? 'scale-[1.15] border-royal-indigo shadow-[0_0_0_3px_rgba(57,73,171,0.18)]'
                    : 'scale-100 border-transparent'
                }`}
                aria-hidden
              >
                {emoji}
              </span>
              <span className="mt-2 text-xs font-medium text-gray-600">{label}</span>
            </button>
          )
        })}
      </div>
      {validationError && (
        <div className="mt-4 text-center text-sm text-red-600" tabIndex={-1}>
          {validationError}
        </div>
      )}
    </div>
  )
}
